/* ------------------------------------------------------------------ */
/*  Scoring – TP/TN/FP/FN, precision/recall/F1, grading              */
/* ------------------------------------------------------------------ */

import type {
  FullTestResult,
  ScenarioLogLine,
  ConfusionMatrix,
  ChallengeScore,
  Grade,
  LineClassification,
} from './types';

/** Points per classification */
const POINTS = { tp: 10, tn: 5, fp: -5, fn: -10 } as const;

/** Grade thresholds based on F1 score */
const GRADE_THRESHOLDS: { min: number; grade: Grade; label: string }[] = [
  { min: 0.95, grade: 'S', label: 'Wazuh Master' },
  { min: 0.8, grade: 'A', label: 'Detection Engineer' },
  { min: 0.6, grade: 'B', label: 'Analyst' },
  { min: 0.4, grade: 'C', label: 'Apprentice' },
  { min: 0.0, grade: 'D', label: 'Needs Practice' },
];

/** Classify each log line as TP/TN/FP/FN */
export function classifyLines(
  result: FullTestResult,
  scenarioLogs: ScenarioLogLine[]
): LineClassification[] {
  return result.lines.map((line, i) => {
    const expected = scenarioLogs[i]?.malicious ?? false;
    const actual = line.firedRules.length > 0;

    let category: LineClassification['category'];
    if (expected && actual) category = 'tp';
    else if (!expected && !actual) category = 'tn';
    else if (!expected && actual) category = 'fp';
    else category = 'fn';

    return {
      lineIndex: i,
      raw: line.raw,
      expected,
      actual,
      category,
      reason: scenarioLogs[i]?.reason ?? '',
    };
  });
}

/** Build confusion matrix from classifications */
export function buildMatrix(classifications: LineClassification[]): ConfusionMatrix {
  const matrix: ConfusionMatrix = { tp: 0, tn: 0, fp: 0, fn: 0 };
  for (const c of classifications) {
    matrix[c.category]++;
  }
  return matrix;
}

/** Calculate full challenge score */
export function calculateScore(
  result: FullTestResult,
  scenarioLogs: ScenarioLogLine[]
): ChallengeScore {
  const classifications = classifyLines(result, scenarioLogs);
  const matrix = buildMatrix(classifications);

  const precision = matrix.tp + matrix.fp > 0 ? matrix.tp / (matrix.tp + matrix.fp) : 0;

  const recall = matrix.tp + matrix.fn > 0 ? matrix.tp / (matrix.tp + matrix.fn) : 0;

  const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;

  const points =
    matrix.tp * POINTS.tp +
    matrix.tn * POINTS.tn +
    matrix.fp * POINTS.fp +
    matrix.fn * POINTS.fn;

  const maxPoints =
    scenarioLogs.filter((l) => l.malicious).length * POINTS.tp +
    scenarioLogs.filter((l) => !l.malicious).length * POINTS.tn;

  const { grade, label } =
    GRADE_THRESHOLDS.find((t) => f1 >= t.min) ??
    GRADE_THRESHOLDS[GRADE_THRESHOLDS.length - 1];

  return {
    matrix,
    precision,
    recall,
    f1,
    points,
    maxPoints,
    grade,
    gradeLabel: label,
  };
}
